/**
 * Custom-column cell for `text` columns.
 *
 * Single-line truncated display in the row; clicking opens a popover with a
 * resizable textarea floating above the row (Monday-style). Saves on close
 * (click outside / Esc). Cmd/Ctrl+Enter also commits. Plain Enter inserts a
 * newline — text values can be multi-line.
 */
import { Popover, PopoverContent, PopoverTrigger, Textarea } from '@monksflow/monks-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EM_DASH } from './constants';

interface TextCustomCellProps {
  value: string | undefined;
  onChange: (next: string | null) => void;
}

export function TextCustomCell({ value, onChange }: TextCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Keep draft in sync with external value updates as long as the editor
  // isn't open. While editing we own the buffer.
  useEffect(() => {
    if (!open) setDraft(value ?? '');
  }, [value, open]);

  // Autofocus + caret-to-end when the popover opens.
  useEffect(() => {
    if (open && textareaRef.current) {
      const ta = textareaRef.current;
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    }
  }, [open]);

  const commit = (next: string) => {
    const trimmed = next.trim();
    const current = (value ?? '').trim();
    if (trimmed === current) return;
    onChange(trimmed === '' ? null : trimmed);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) commit(draft);
        setOpen(nextOpen);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          // A text value is secondary to the item it hangs off, so it rests
          // muted and only comes up to full contrast under the cursor. Without
          // the resting token it inherited the row's foreground and the hover
          // did nothing.
          className="block w-full truncate text-center text-sm font-normal text-muted-foreground transition-colors hover:text-foreground"
        >
          {value && value.length > 0 ? (
            value
          ) : (
            <span className="text-muted-foreground">{EM_DASH}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-fit min-w-80 max-w-[90vw] p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <Textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(value ?? '');
              setOpen(false);
            } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              commit(draft);
              setOpen(false);
            }
          }}
          rows={4}
          className="min-h-24 resize border-0 text-sm font-normal shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          placeholder={t('trackersBoard.textCustomCell.typeHere')}
        />
      </PopoverContent>
    </Popover>
  );
}
